import { useEffect, useState } from "react";
import { FileText, Download } from "lucide-react";
import AppLayout from "@/components/AppLayout";
import PageHeader from "@/components/PageHeader";
import DataTable, { Column } from "@/components/DataTable";
import { LoadingState, EmptyState } from "@/components/LoadingState";
import { buildingApi, invoiceApi, unitApi } from "@/api/endpoints";
import { useOrgId } from "@/hooks/useOrgId";
import type { BuildingSummary, Invoice, Unit } from "@/types";
import { formatZar } from "@/utils/format";

export default function InvoicesPage() {
  const orgId = useOrgId();
  const [buildings, setBuildings] = useState<BuildingSummary[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [buildingId, setBuildingId] = useState<string>("");
  const [unitId, setUnitId] = useState<string>("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    buildingApi.list(orgId).then((b) => {
      setBuildings(b);
      setLoading(false);
    });
  }, [orgId]);

  useEffect(() => {
    setUnits([]);
    setUnitId("");
    setInvoices([]);
    if (buildingId) unitApi.list(buildingId).then(setUnits);
  }, [buildingId]);

  useEffect(() => {
    setInvoices([]);
    if (unitId) invoiceApi.listForUnit(unitId).then(setInvoices);
  }, [unitId]);

  const columns: Column<Invoice>[] = [
    {
      key: "period",
      header: "Period",
      render: (i) => `${i.period_start} → ${i.period_end}`,
    },
    {
      key: "total",
      header: "Total",
      render: (i) => formatZar(i.total_cost),
    },
    {
      key: "status",
      header: "Status",
      render: (i) => {
        const map: Record<string, string> = {
          draft: "bg-slate-100 text-slate-700",
          issued: "bg-blue-50 text-blue-700",
          paid: "bg-brand-50 text-brand-800",
        };
        return <span className={`badge ${map[i.status]}`}>{i.status}</span>;
      },
    },
    {
      key: "actions",
      header: "",
      render: (i) => (
        <a className="btn-secondary text-xs" href={i.pdf_url ?? "#"} target="_blank" rel="noreferrer">
          <Download size={14} /> PDF
        </a>
      ),
    },
  ];

  return (
    <AppLayout>
      <PageHeader title="Invoices" subtitle="Billing per unit, calculated from tariffs × consumption" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="card p-4">
          <label className="label">Building</label>
          <select className="input" value={buildingId} onChange={(e) => setBuildingId(e.target.value)}>
            <option value="">Select building…</option>
            {buildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
        <div className="card p-4">
          <label className="label">Unit</label>
          <select className="input" value={unitId} onChange={(e) => setUnitId(e.target.value)} disabled={!units.length}>
            <option value="">Select unit…</option>
            {units.map((u) => <option key={u.id} value={u.id}>{u.unit_number}</option>)}
          </select>
        </div>
      </div>
      {loading ? (
        <LoadingState />
      ) : unitId ? (
        <DataTable columns={columns} rows={invoices} emptyMessage="No invoices for this unit" />
      ) : (
        <EmptyState title="Select a building and unit" hint="Invoices are scoped to individual units." />
      )}
    </AppLayout>
  );
}
